/**
 * NeuerVersichererDialog — create a Versicherer by UID (P2 hardening 02).
 *
 * Name autocomplete via the existing register suggester (GET /register/firm-names): picking a
 * suggestion fills carrier_uid + display_name; a manual UID input is the regex-validated fallback.
 * Submit → POST /carriers. On 409 (versicherer_exists) it navigates to the existing insurer with a
 * "bereits vorhanden" toast; on 201 it navigates to /versicherer/$uid. Live mode only (the create
 * endpoint writes the tenant row + enqueues the Zefix enrichment).
 *
 * [Source: apps/web/src/features/customers/components/NewCustomerWizard.tsx] (FirmNameAutocomplete usage)
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { FirmNameAutocomplete } from '../../../components/ui/FirmNameAutocomplete';
import { apiClient, ApiError } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';

const CHE_UID = /^CHE-\d{3}\.\d{3}\.\d{3}$/;

export function NeuerVersichererDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [uid, setUid] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uidValid = CHE_UID.test(uid.trim());

  async function submit() {
    if (!uidValid) {
      setError('Bitte eine gültige UID eingeben (CHE-123.456.789).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await apiClient.carriers.create({
        carrier_uid: uid.trim(),
        display_name: name.trim() === '' ? null : name.trim(),
      });
      await qc.invalidateQueries({ queryKey: qk.carriers('live') });
      onClose();
      void navigate({ to: `/versicherer/${created.carrier_uid}` as never });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const existingUid = (err.data as { carrier_uid?: string } | undefined)?.carrier_uid ?? uid.trim();
        onClose();
        void navigate({ to: `/versicherer/${existingUid}` as never });
        return;
      }
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Neuer Versicherer">
      <div className="w-full max-w-md rounded-lg border bg-card p-5 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Neuer Versicherer</h2>
          <button type="button" onClick={onClose} aria-label="Schliessen" className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary">
            <X className="size-4" />
          </button>
        </div>

        {error !== null && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Name (aus dem Handelsregister)</span>
            <FirmNameAutocomplete
              value={name}
              onChange={setName}
              onPick={(row) => {
                setName(row.name);
                if (row.uid) setUid(row.uid);
              }}
              placeholder="AXA Versicherungen AG"
              aria-label="Versicherer-Name"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">UID *</span>
            <input
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              placeholder="CHE-123.456.789"
              aria-label="UID"
              className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {uid.trim() !== '' && !uidValid && (
              <span className="text-xs text-destructive">Format: CHE-123.456.789</span>
            )}
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={saving || !uidValid}>
            {saving ? 'Anlegen…' : 'Anlegen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
